import { useCallback, useMemo } from 'react';
import type { TFunction } from 'i18next';
import { customInstance } from '@/api/customInstance';
import { inventoryVehicleList } from '@/api/generated/inventory-vehicles/inventory-vehicles';
import type { VehicleListItem } from '@/api/generated/schemas';
import { ExpirySeverity, VehicleStatus } from '@/api/generated/schemas';
import type { CodebookRef } from '@/modules/codebooks';
import { Badge } from '@/shared/ui';
import {
  col,
  filter,
  type ListEnumOptionsSource,
  type ListExportColumn,
  type ListPageConfig,
  type ListQueryRequest,
} from '@/shared/ui/list-page';
import { NavListPage } from '@/shared/navigation/react/NavListPage';
import { useNavigator } from '@/shared/navigation/react/useNavigator';

import { vehicleCategorySelector, vehicleGroupSelector } from '../../components/vehicleCodebooks';
import { vehicleDetailAgenda } from '../detail/vehicleDetailAgenda';
import { formatAvailability, formatExpiry } from '../internal/vehicleFormatting';
import { VEHICLE_LIST_KEY } from '../internal/vehiclesQueryKeys';
import { vehicleListAgenda } from './vehicleListAgenda';

/** Barevný tón odznaku podle naléhavosti expirace (viz backend `ExpirySeverity`). */
const SEVERITY_TONE = {
  [ExpirySeverity.None]: undefined,
  [ExpirySeverity.Warning]: 'warning',
  [ExpirySeverity.Expired]: 'danger',
} as const;

const STATUS_TONE = {
  [VehicleStatus.Active]: 'success',
  [VehicleStatus.Disabled]: 'neutral',
  [VehicleStatus.Maintenance]: 'info',
} as const;

/** Statické options enum filtru (hodnoty jsou součástí API kontraktu, ne číselník). */
function staticOptions(options: { id: string; label: string }[]): ListEnumOptionsSource {
  return { useOptions: () => ({ options, isLoading: false }) };
}

/** Sloupec expirace — datum se zbývajícími dny a odznak naléhavosti. */
function expiryColumn(
  key: 'insuranceExpiry' | 'inspectionExpiry' | 'tachographInspectionExpiry',
  severityKey: 'insuranceSeverity' | 'inspectionSeverity' | 'tachographInspectionSeverity',
  t: TFunction
) {
  return col.custom<VehicleListItem>({
    key,
    label: t(`vehicleList.cols.${key}`),
    w: 168,
    sortKey: key,
    cell: (item) => {
      const text = formatExpiry(t, item[key]);
      const tone = SEVERITY_TONE[item[severityKey]];
      return tone ? <Badge tone={tone}>{text}</Badge> : <span>{text}</span>;
    },
  });
}

/** Export vozů do XLSX — Orval u binárky vrací string, voláme proto customInstance s blobem. */
function exportVehicles(
  request: ListQueryRequest,
  columns: ListExportColumn[],
  maxRows: number,
  signal?: AbortSignal
): Promise<Blob> {
  return customInstance<Blob>({
    url: '/api/inventory/vehicles/list/export',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: { request, columns, maxRows },
    responseType: 'blob',
    signal,
  });
}

/**
 * Šablona ListPage vozů. Filtr expirací řeší BE lokálně (`VehicleListHandler`).
 */
function buildVehicleListConfig(
  openVehicle: (id: number | undefined) => void
): ListPageConfig<VehicleListItem> {
  return {
    key: VEHICLE_LIST_KEY,
    i18n: { namespace: 'inventory' },
    api: {
      list: (request, signal) => inventoryVehicleList(request, signal),
      export: exportVehicles,
    },
    rowKey: (item) => item.vehicleId,
    defaultSort: { column: 'plate', desc: false },
    fulltext: { placeholder: (t) => t('vehicleList.search') },
    exportFileName: (t) => t('vehicleList.exportFile'),
    onOpen: (item) => openVehicle(item.vehicleId),
    create: { label: (t) => t('vehicleList.new'), onClick: () => openVehicle(undefined) },
    columns: (t) => [
      col.strong<VehicleListItem>('plate', {
        label: t('vehicleList.cols.plate'),
        w: 120,
        frozen: true,
      }),
      col.text<VehicleListItem>('name', {
        label: t('vehicleList.cols.name'),
        w: 220,
        frozen: true,
      }),
      col.text<VehicleListItem>('categoryName', {
        label: t('vehicleList.cols.category'),
        w: 150,
        sortKey: 'category',
      }),
      col.text<VehicleListItem>('groupName', {
        label: t('vehicleList.cols.group'),
        w: 170,
        sortKey: 'group',
      }),
      col.custom<VehicleListItem>({
        key: 'status',
        label: t('vehicleList.cols.status'),
        w: 104,
        sortKey: 'status',
        cell: (item) => (
          <Badge tone={STATUS_TONE[item.status]}>
            {t(`vehicleStatus.${item.status.toLowerCase()}`)}
          </Badge>
        ),
      }),
      col.custom<VehicleListItem>({
        // Odvozený text ze dvou zdrojových dat — na serveru neexistuje sloupec, proto
        // není řaditelný. Klíč míří na `commissioningDate`, aby export (čte hodnoty z řádkového DTO
        // reflexí podle klíče) nesl aspoň zdrojové datum místo prázdné buňky.
        key: 'commissioningDate',
        label: t('vehicleList.cols.availability'),
        w: 170,
        sortable: false,
        cell: (item) => (
          <span>{formatAvailability(t, item.commissioningDate, item.retirementDate)}</span>
        ),
      }),
      expiryColumn('insuranceExpiry', 'insuranceSeverity', t),
      expiryColumn('inspectionExpiry', 'inspectionSeverity', t),
      expiryColumn('tachographInspectionExpiry', 'tachographInspectionSeverity', t),
    ],
    filterGroups: (t) => [
      { id: 'basic', label: t('vehicleList.groups.basic') },
      { id: 'expiry', label: t('vehicleList.groups.expiry') },
    ],
    filters: (t) => [
      filter.selector<CodebookRef>('category', {
        label: t('vehicleList.filters.category'),
        group: 'basic',
        freq: true,
        selector: vehicleCategorySelector.selector,
        getId: (item) => item.id,
        getLabel: (item) => item.name,
      }),
      filter.selector<CodebookRef>('group', {
        label: t('vehicleList.filters.group'),
        group: 'basic',
        freq: true,
        selector: vehicleGroupSelector.selector,
        getId: (item) => item.id,
        getLabel: (item) => item.name,
      }),
      filter.enum('status', {
        label: t('vehicleList.filters.status'),
        group: 'basic',
        freq: true,
        options: staticOptions([
          { id: VehicleStatus.Active, label: t('vehicleStatus.active') },
          { id: VehicleStatus.Disabled, label: t('vehicleStatus.disabled') },
          { id: VehicleStatus.Maintenance, label: t('vehicleStatus.maintenance') },
        ]),
      }),
      // Doménový filtr expirací: typ + jeden ze dvou režimů (absolutní datum NEBO počet dnů).
      filter.enum('expiryType', {
        label: t('vehicleList.filters.expiryType'),
        group: 'expiry',
        options: staticOptions([
          { id: 'All', label: t('vehicleList.expiryType.all') },
          { id: 'Insurance', label: t('vehicleList.expiryType.insurance') },
          { id: 'Inspection', label: t('vehicleList.expiryType.inspection') },
          { id: 'Tachograph', label: t('vehicleList.expiryType.tachograph') },
        ]),
      }),
      filter.daterange('expiryDate', {
        label: t('vehicleList.filters.expiryDate'),
        group: 'expiry',
      }),
      // Dvě nezávislé meze „počet dnů do expirace"; operátor pole (≥ / = / ≤) určuje, o kterou mez
      // jde, a backend je protíná. Absolutní i denní režim se navzájem vylučují.
      filter.number('expiryDaysFrom', {
        label: t('vehicleList.filters.expiryDaysFrom'),
        group: 'expiry',
        unit: t('vehicleList.daysUnit'),
      }),
      filter.number('expiryDaysTo', {
        label: t('vehicleList.filters.expiryDaysTo'),
        group: 'expiry',
        unit: t('vehicleList.daysUnit'),
      }),
    ],
  };
}

/**
 * Agenda seznamu vozů. Otevření řádku i akce „Nový vůz" míří na `vehicleDetailAgenda` přes
 * `openDetail` (výběr v listu určuje, co je otevřené v detail panelu).
 */
export function VehicleListPage({ layerId }: { layerId: string }) {
  const navigator = useNavigator();

  const openVehicle = useCallback(
    (id: number | undefined) => {
      void navigator.openDetail(vehicleDetailAgenda, { vehicleId: id });
    },
    [navigator]
  );

  const config = useMemo(() => buildVehicleListConfig(openVehicle), [openVehicle]);

  return <NavListPage agenda={vehicleListAgenda} layerId={layerId} config={config} />;
}
